import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "@/contexts/AuthContext";
import { UsersThree, Plus, Link as LinkIcon, Copy, Trash, Crown, User as UserIcon, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function TeamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const isOwner = user?.role === "owner";

  const load = async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        axios.get(`${API}/app/team`),
        isOwner ? axios.get(`${API}/app/team/invitations`) : Promise.resolve({ data: [] }),
      ]);
      setMembers(m.data);
      setInvites(i.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const generate = async () => {
    setCreating(true);
    try {
      const payload = { role: "member" };
      const email = inviteEmail.trim();
      if (email) payload.email = email;
      const { data } = await axios.post(`${API}/app/team/invitations`, payload);
      if (data.email_sent) {
        toast.success(`Einladung per E-Mail an ${email} versendet`);
      } else {
        toast.success("Einladungslink erstellt");
      }
      // copy immediately
      const full = `${window.location.origin}${data.link}`;
      try {
        await navigator.clipboard.writeText(full);
        toast.success("Link in Zwischenablage kopiert");
      } catch {
        /* ignore */
      }
      setInviteEmail("");
      load();
    } catch (err) {
      if (err.response?.status === 402) {
        const d = err.response.data?.detail || {};
        toast.error(d.message || "Sitz-Limit erreicht.", {
          action: { label: "Upgrade", onClick: () => navigate("/preise") },
          duration: 8000,
        });
      } else {
        const d = err.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Konnte Einladung nicht erstellen");
      }
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Einladung widerrufen?")) return;
    await axios.delete(`${API}/app/team/invitations/${token}`);
    toast.success("Einladung widerrufen");
    load();
  };

  const copyLink = async (link) => {
    const full = `${window.location.origin}${link}`;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Link kopiert");
    } catch {
      toast.error("Konnte Link nicht kopieren");
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest-900">Team</h1>
          <p className="mt-2 font-body text-[15px] text-[#4A524C]">
            {isOwner
              ? "Laden Sie Mitarbeiter zu Ihrem Betrieb ein. Mitarbeiter sehen und erfassen dieselben Daten."
              : "Übersicht aller Personen, die zu diesem Betrieb gehören."}
          </p>
        </div>
        {isOwner && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              generate();
            }}
            className="flex flex-wrap items-center gap-2 bg-white border border-bone-200 rounded-2xl p-2"
          >
            <div className="flex items-center gap-2 pl-2">
              <EnvelopeSimple size={18} weight="duotone" className="text-forest-600" />
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="mitarbeiter@betrieb.de (optional)"
                data-testid="team-invite-email"
                className="font-body text-sm bg-transparent outline-none placeholder:text-[#9CA69F] min-w-[220px]"
              />
            </div>
            <button
              type="submit"
              disabled={creating}
              data-testid="team-create-invite"
              className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-medium px-4 py-2 rounded-xl transition"
            >
              <Plus size={16} weight="bold" />
              {creating ? "Erstelle …" : inviteEmail.trim() ? "Einladung senden" : "Link erzeugen"}
            </button>
          </form>
        )}
      </div>

      {/* Members */}
      <h2 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-forest-700 mb-3">
        Mitglieder
      </h2>
      {loading ? (
        <div className="font-body text-sm text-[#7A827C]">Lade …</div>
      ) : (
        <ul className="space-y-3 mb-10" data-testid="team-members">
          {members.map((m) => (
            <li
              key={m.id}
              data-testid={`team-member-${m.id}`}
              className="bg-white rounded-2xl border border-bone-200 p-5 flex items-center gap-4"
            >
              <div className="w-11 h-11 rounded-full bg-forest-50 border border-forest-100 flex items-center justify-center">
                {m.role === "owner" ? (
                  <Crown size={20} weight="duotone" className="text-terracotta" />
                ) : (
                  <UserIcon size={20} weight="duotone" className="text-forest-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base font-semibold text-forest-900 truncate">
                  {m.name}
                </div>
                <div className="font-body text-sm text-[#4A524C] truncate">{m.email}</div>
              </div>
              <span
                className={`text-[11px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full ${
                  m.role === "owner"
                    ? "bg-terracotta/10 text-terracotta"
                    : "bg-forest-50 text-forest-700"
                }`}
              >
                {m.role === "owner" ? "Inhaber" : "Mitarbeiter"}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Invitations */}
      {isOwner && (
        <>
          <h2 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-forest-700 mb-3">
            Offene Einladungen
          </h2>
          {invites.length === 0 ? (
            <div className="bg-white rounded-2xl border border-bone-200 p-8 text-center text-sm text-[#7A827C]">
              <UsersThree
                size={26}
                weight="duotone"
                className="text-forest-700 mx-auto mb-2"
              />
              Erstellen Sie einen Einladungslink und teilen Sie ihn per E-Mail,
              WhatsApp oder beliebigem Kanal mit Ihren Mitarbeitern.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="team-invites">
              {invites.map((iv) => {
                const valid =
                  !iv.used_at && new Date(iv.expires_at) > new Date();
                const fullLink = `${window.location.origin}${iv.link}`;
                return (
                  <li
                    key={iv.token}
                    data-testid={`invite-${iv.token}`}
                    className="bg-white rounded-2xl border border-bone-200 p-5 flex items-center gap-4 flex-wrap"
                  >
                    <div className="w-11 h-11 rounded-xl bg-bone-100 border border-bone-200 flex items-center justify-center">
                      {iv.email ? (
                        <EnvelopeSimple size={20} weight="duotone" className="text-forest-700" />
                      ) : (
                        <LinkIcon size={20} weight="duotone" className="text-forest-700" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {iv.email && (
                        <div className="font-body text-sm font-semibold text-forest-900 truncate flex items-center gap-1.5">
                          {iv.email}
                          {!iv.used_at && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-forest-700 bg-forest-50 px-1.5 py-0.5 rounded"
                              title="E-Mail versendet"
                            >
                              <CheckCircle size={10} weight="fill" /> gesendet
                            </span>
                          )}
                        </div>
                      )}
                      <div className="font-body text-xs text-[#7A827C] truncate">
                        {fullLink}
                      </div>
                      <div className="font-body text-xs text-[#7A827C] mt-0.5">
                        {iv.used_at
                          ? `Verwendet am ${new Date(iv.used_at).toLocaleDateString("de-DE")}`
                          : valid
                          ? `Gültig bis ${new Date(iv.expires_at).toLocaleDateString("de-DE")}`
                          : "Abgelaufen"}
                      </div>
                    </div>
                    {!iv.used_at && valid && (
                      <>
                        <button
                          onClick={() => copyLink(iv.link)}
                          className="inline-flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 px-3 py-1.5 rounded-lg hover:bg-bone-100"
                          data-testid={`invite-copy-${iv.token}`}
                        >
                          <Copy size={14} /> Kopieren
                        </button>
                        <button
                          onClick={() => revoke(iv.token)}
                          className="inline-flex items-center gap-1.5 text-sm text-[#7A827C] hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50"
                          data-testid={`invite-revoke-${iv.token}`}
                        >
                          <Trash size={14} /> Widerrufen
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
